import { Api } from "@/api";
import type { BrandGroup } from "@/generated/graphql/b2c";
import { StaticPageContentDocument, StaticPageContentType } from "@/generated/graphql.lazy/b2c.ts";
import { Context } from "@/services/context.ts";
import { sanitizeStaticPage } from "static-page-sanitizer";

export type StaticPagePropItem = { key: string; value: string };

export interface StaticPagePropsProviderInterface {
  getStaticPageProps(type: string): StaticPagePropItem[] | undefined;
}

export interface BrandGroupsProviderInterface {
  getBrandGroups(): BrandGroup[];
}

export function staticPagePropsToMap(
  items: StaticPagePropItem[] | undefined
): Readonly<Record<string, string>> {
  if (!items?.length) {
    return {};
  }
  return Object.fromEntries(items.map((item) => [item.key, item.value]));
}

export const STATIC_PAGE_LISTENER_NAME = "staticPage";

export const StaticPagesType = {
  aboutUs: "about-us",
  contactUs: "contact-us",
  help: "help",
  whyCustomersTrustUs: 'why-customers-trust-us',
  whatWeDo: "what-we-do",
  whoWeServe: "who-we-serve",
  privacyPolicy: "privacy-policy",
  paymentTerms: "payment-terms",
  whoWeAre: "who-we-are",
  productRange: "product-range",
};

export class StaticPages implements StaticPagePropsProviderInterface, BrandGroupsProviderInterface {
  loading = true;

  protected api: Api;
  protected context: Context;

  private pagesMapping: Map<string, string>;
  private pagesPropsMapping: Map<string, Array<{ key: string; value: string }>>;
  private brandGroups: BrandGroup[] = [];

  constructor(context: Context, api: Api) {
    this.api = api;
    this.context = context;
    this.pagesMapping = new Map();
    this.pagesPropsMapping = new Map();

    this.update();
  }

  fetchStaticPage = async (type: string) => {
    try {
      const payload = await this.requestCurrentCart(type);

      if (!payload) {
        return null;
      }

      await this.updateStaticPage(type, payload);
    } catch (error: any) {
      throw new Error(error.message);
    } finally {
      this.loading = false;
      this.update();
    }
  };

  async updateStaticPage(type: string, payload: StaticPageContentType) {
    this.pagesMapping.set(type, await sanitizeStaticPage(payload.content));
    this.pagesPropsMapping.set(type, payload.salesChannelTemplateParams);
    for (const brandGroup of payload.brandGroups ?? []) {
      this.brandGroups.push(brandGroup);
    }
  }

  getBrandGroups() {
    return this.brandGroups;
  }

  has = (type: string) => this.pagesMapping.has(type);

  getStaticPage = (type: string) => {
    return this.pagesMapping.get(type) ?? "";
  };

  protected requestCurrentCart = async (type: string) => {
    const cartResponse = await this.api.client.query({
      query: StaticPageContentDocument,
      variables: {
        input: {
          name: type,
        },
      },
    });

    return cartResponse.data.StaticPageContent as StaticPageContentType;
  };

  protected update = () => {
    this.context.notify(STATIC_PAGE_LISTENER_NAME);
  };

  getStaticPageProps = (type: string) => {
    return this.pagesPropsMapping.get(type);
  };
}
